import React from "react";
import { X, Download, Upload, BadgeCheck, Pencil, Trash2 } from "lucide-react";
import { colors, shadows } from "../../theme";
import { useStudio } from "../../store/useStudio";
import { PILLAR_BY_ID } from "../../data/matrices";
import { outOfBounds, LITERATURE_BOUNDS, structuralBoundKey } from "../../lib/citations";
import { systemNeedsGrowLights } from "../../lib/kbLogic";
import { styles, MINT } from "./tokens";
import { cropIcon } from "./icons";
import { blockDemand, OVERRIDE_METRIC_ROWS, systemDefaultMetrics } from "./helpers";
import { Metric, CiteTag, ReferencesButton, FooterStat } from "./primitives";
import { ShapeModeToggle } from "./drafting";

// Phase 2 (item 3) — manual L×W×H numeric override for a drafted block (rebuilds the 3D volume live).
export function BlockDims({ b }) {
  const requestBlockResize = useStudio((s) => s.requestBlockResize);
  const requestBlockRotate = useStudio((s) => s.requestBlockRotate);
  const dims = React.useMemo(() => {
    const g = b.geom;
    let L = 0, W = 0;
    if (g?.shape?.type === "rect") { L = g.shape.aC; W = g.shape.bC; }
    else if (g?.shape?.outline?.length) {
      const xs = g.shape.outline.map((p) => p[0]), ys = g.shape.outline.map((p) => p[1]);
      L = Math.max(...xs) - Math.min(...xs); W = Math.max(...ys) - Math.min(...ys);
    } else if (b.footprint) {
      // Unplaced proprietary instance (e.g. QT POD): show its hardcoded footprint.
      L = b.footprint.length; W = b.footprint.width;
    }
    return { L: +Number(L).toFixed(2), W: +Number(W).toFixed(2), H: b.height };
  }, [b.geom, b.height, b.footprint]);
  const [local, setLocal] = React.useState(dims);
  React.useEffect(() => setLocal(dims), [dims]);
  const locked = !!b.footprint; // proprietary fixed-footprint (e.g. QT POD) — dims are enforced
  const commit = (next) => {
    setLocal(next);
    if (!locked && next.L > 0 && next.W > 0 && next.H > 0) requestBlockResize({ id: b.id, length: next.L, width: next.W, height: next.H });
  };
  const field = (key, label) => (
    <div style={styles.dimField}>
      <span style={styles.dimLabel}>{label}</span>
      <input
        type="number" min="0.2" step="0.1" data-testid={`block-dim-${key}-${b.id}`}
        readOnly={locked} disabled={locked}
        style={{ ...styles.dimInput, ...(locked ? { background: "rgba(0,0,0,0.04)", color: colors.textTertiary, cursor: "not-allowed" } : {}) }} value={local[key] ?? ""}
        onChange={(e) => setLocal((s) => ({ ...s, [key]: Number(e.target.value) }))}
        onBlur={() => commit(local)}
        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
  const rot = ((Number(b.rotation) || 0) % 360 + 360) % 360;
  const setRot = (deg) => { const a = ((Math.round(deg / 5) * 5) % 360 + 360) % 360; requestBlockRotate({ id: b.id, angle: a }); };
  return (
    <div onClick={(e) => e.stopPropagation()} data-testid={`block-dims-${b.id}`}>
      <div style={styles.dimRow}>
        {field("L", "LENGTH (m)")}
        {field("W", "WIDTH (m)")}
        {field("H", "HEIGHT (m)")}
      </div>
      <div style={styles.dimRow}>
        <div style={{ ...styles.dimField, flex: 1 }}>
          <span style={styles.dimLabel}>ROTATION (°, 5° steps)</span>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <button type="button" data-testid={`block-rotate-minus-${b.id}`} title="Rotate −5°"
              style={styles.rotStepBtn} onClick={(e) => { e.stopPropagation(); setRot(rot - 5); }}>−5°</button>
            <input type="number" min="0" max="355" step="5" data-testid={`block-rotate-input-${b.id}`}
              style={{ ...styles.dimInput, width: 56, textAlign: "center" }} value={rot}
              onChange={(e) => setRot(Number(e.target.value))}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }} />
            <button type="button" data-testid={`block-rotate-plus-${b.id}`} title="Rotate +5°"
              style={styles.rotStepBtn} onClick={(e) => { e.stopPropagation(); setRot(rot + 5); }}>+5°</button>
            <button type="button" data-testid={`block-rotate-${b.id}`} title="Rotate 90°"
              style={{ ...styles.rotateBtn, marginLeft: 2 }} onClick={(e) => { e.stopPropagation(); setRot(rot + 90); }}>⟳ 90°</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// (structural load, water, energy, embodied carbon) each with a provenance tag, and the
// resulting ABSOLUTE demand for this instance's area (Area × baseline). The structural-load
// literature estimate (limited data) is user-editable.
export function SystemBaselines({ system, area, block }) {
  const override = useStudio((s) => s.systemWeightOverride[system?.id]);
  const finOv = useStudio((s) => (block ? s.financialOverrides[block.id] : null));
  const setBlockMetric = useStudio((s) => s.setBlockMetric);
  const b = system?.baselines;
  if (!b) return null;
  const w = b.operational_weight_kgm2;
  const weightVal = override != null && override !== "" ? Number(override) : w?.value;
  const water = b.water_lpm2d, energy = b.energy_kwhm2d, carbon = b.embodied_carbon_kgm2;
  const fin = system?.financials || { capex_per_m2: 600, opex_per_m2_yr: 70 };
  const mo = block?.metricOverrides || {};
  const fo = finOv || {};
  const eff = (k, base) => (mo[k] != null && mo[k] !== "" ? Number(mo[k]) : base);
  const waterV = eff("water", water?.value), energyV = eff("energy", energy?.value), carbonV = eff("carbon", carbon?.value);
  const set = (k, v) => setBlockMetric(block?.id, system.id, k, v);
  const A = Number(area) || 0;
  const boundKey = structuralBoundKey(system);
  const sBound = LITERATURE_BOUNDS[boundKey];
  const bound = outOfBounds(boundKey, weightVal);
  const wv = Number(weightVal);
  // True overload = at/over the 1200 kg/m² ceiling → red engineering caution. "Near" = ≥90% of max.
  const overload = !Number.isNaN(wv) && wv > sBound.max;
  const nearOverload = !Number.isNaN(wv) && wv >= 0.9 * sBound.max && wv <= sBound.max;
  const stop = (e) => e.stopPropagation();

  // key, label, controlled value, placeholder(db), unit, cite
  const rows = [
    ["structural", "Structural Load", override != null ? override : "", w?.value, "kg/m²", w?.cite],
    ["water", "Baseline Water", mo.water ?? "", water?.value, "L/m²·d", water?.cite],
    ["energy", "Baseline Energy", mo.energy ?? "", energy?.value, "kWh/m²·d", energy?.cite],
    ["carbon", "Embodied Carbon", mo.carbon ?? "", carbon?.value, "kgCO₂e/m²", carbon?.cite],
    ["capex_per_m2", "CAPEX", fo.capex_per_m2 ?? "", fin.capex_per_m2, "$/m²", fin.capex_cite],
    ["opex_per_m2_yr", "OPEX", fo.opex_per_m2_yr ?? "", fin.opex_per_m2_yr, "$/m²·yr", fin.opex_cite],
  ];

  return (
    <div style={styles.baselineBox} data-testid={`system-baselines-${system.id}`}>
      <div style={styles.baselineHead}>DATABASE METRICS</div>
      <div style={styles.baselineSub}>Editable per instance; updates the total live.</div>
      <div style={styles.bHead} aria-hidden>
        <span>Metric</span>
        <span style={{ textAlign: "right" }}>Value</span>
        <span>Unit</span>
        <span style={{ textAlign: "right" }}>Reference</span>
      </div>
      {rows.map(([k, label, val, dbv, unit, cite]) => (
        <div key={k} style={styles.bRow} data-testid={`baseline-${k}-${system.id}`}>
          <span style={styles.bLabel}>{label}</span>
          <input type="number" min="0" value={val} placeholder={dbv != null ? String(dbv) : "—"}
            data-testid={`metric-input-${k}-${system.id}`}
            style={{ ...styles.weightInput, ...(k === "structural" && overload ? styles.weightInputOverload : {}) }}
            onClick={stop} onChange={(e) => set(k, e.target.value)} />
          <span style={styles.bUnit}>{unit}</span>
          <span style={styles.bCiteCell}>
            {cite === "Vendor data"
              ? <span style={styles.bVendor} data-testid={`baseline-vendor-${k}-${system.id}`} title="Manufacturer / commercial / project-supplied figure — not peer-reviewed. Edit if you have verified data.">vendor data</span>
              : cite
                ? <CiteTag cite={cite} />
                : <span style={styles.bUserDef} data-testid={`baseline-userdef-${k}-${system.id}`} title="No high-impact reference for this default; treat it as a user-defined estimate and edit the value.">user-defined</span>}
          </span>
          {k === "structural" && (
            <span style={styles.bRangeHint} data-testid={`metric-range-${system.id}`}>
              expected {sBound.min}–{sBound.max} kg/m² · {boundKey === "operational_weight_elevated" ? "elevated / rooftop" : "ground-level"}
            </span>
          )}
        </div>
      ))}
      {w?.limited && (
        <div style={styles.limitedNote} data-testid={`weight-limited-${system.id}`}>
          Limited data: peer-reviewed literature estimate. Edit above with your verified figure.
        </div>
      )}
      {overload && (
        <div style={styles.overloadWarn} data-testid={`weight-overload-${system.id}`}>
          ⛔ {weightVal} kg/m² is a structural OVERLOAD — above the {sBound.max} kg/m² ceiling ({sBound.ref}). Verify the structure can bear this load.
        </div>
      )}
      {!overload && nearOverload && (
        <div style={styles.nearOverloadWarn} data-testid={`weight-near-overload-${system.id}`}>
          ⚠ {weightVal} kg/m² is near the {sBound.max} kg/m² structural ceiling — confirm the load path.
        </div>
      )}
      {bound && !overload && (
        <div style={styles.boundWarn} data-testid={`weight-bound-warn-${system.id}`}>
          ⚠ {weightVal} kg/m² is {bound.side === "below" ? "below" : "above"} standard bounds ({bound.min}–{bound.max} {bound.unit}, {bound.ref}).
        </div>
      )}
      {A > 0 && (
        <div style={styles.absDemand} data-testid={`absolute-demand-${system.id}`}>
          <span style={styles.absHead}>ABSOLUTE DEMAND · {A} m²</span>
          <div style={styles.absLine}>
            {weightVal ? `Load ${Math.round(weightVal * A).toLocaleString()} kg` : ""}
            {water ? ` · Water ${Math.round(waterV * A * 365).toLocaleString()} L/yr` : ""}
            {energy ? ` · Energy ${Math.round(energyV * A * 365).toLocaleString()} kWh/yr` : ""}
            {carbon ? ` · Embodied ${Math.round(carbonV * A).toLocaleString()} kgCO₂e` : ""}
          </div>
        </div>
      )}
      {system.citations?.length > 0 && (
        <div style={{ marginTop: 9 }}>
          <ReferencesButton citations={system.citations} title={`${system.label} sources`} label="References" testId={`references-button-${system.id}`} />
        </div>
      )}
    </div>
  );
}

// Editable draft title — pre-filled with an auto summary (Zone · System · area).
// Users can rename each draft so multiple scenarios stay distinguishable.
export function BlockTitle({ b }) {
  const updateSystemBlock = useStudio((s) => s.updateSystemBlock);
  const [editing, setEditing] = React.useState(false);
  const areaM2 = (b.allocatedArea && b.allocatedArea > 0) ? b.allocatedArea : b.area;
  const auto = `${b.locationLabel ? `${b.locationLabel} · ` : ""}${b.systemLabel || "Untitled"}${areaM2 ? ` · ${Math.round(areaM2).toLocaleString()} m²` : ""}`;
  const [val, setVal] = React.useState(b.name || "");
  React.useEffect(() => { setVal(b.name || ""); }, [b.name]);
  const commit = () => { updateSystemBlock(b.id, { name: val.trim() }); setEditing(false); };
  if (editing) {
    return (
      <input
        autoFocus
        value={val}
        placeholder={auto}
        data-testid={`block-name-input-${b.id}`}
        style={styles.blockTitleInput}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") { setVal(b.name || ""); setEditing(false); } }}
      />
    );
  }
  return (
    <button
      type="button"
      style={styles.blockTitleBtn}
      data-testid={`block-name-${b.id}`}
      title="Rename this draft"
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
    >
      <span style={styles.blockTitle}>{(b.name && b.name.trim()) || auto}</span>
      <Pencil size={11} style={{ opacity: 0.45, flexShrink: 0 }} />
    </button>
  );
}

// Per-instance Grow-Light Schedule — only rendered for systems that grow under artificial lighting
// (indoor / hydroponic / proprietary). Each lit instance carries its own photoperiod + lighting share,
// which drives the day/night split of THIS system's operational energy in the Energy loop.
function GrowLightSchedule({ b }) {
  const updateSystemBlock = useStudio((s) => s.updateSystemBlock);
  return (
    <div style={styles.blockBox} data-testid={`grow-light-schedule-${b.id}`} onClick={(e) => e.stopPropagation()}>
      <div style={styles.baselineHead}>GROW-LIGHT SCHEDULE</div>
      <div style={styles.baselineSub}>Artificial lighting for this system. Drives the day/night Energy-loop match.</div>
      <div style={styles.thermalRow}>
        <span style={styles.qLabel}>Photoperiod</span>
        <input type="number" min="1" max="24" value={b.photoperiodH ?? ""} placeholder="16"
          data-testid={`photoperiod-input-${b.id}`} style={styles.areaInput}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => updateSystemBlock(b.id, { photoperiodH: e.target.value === "" ? null : Math.max(1, Math.min(24, Number(e.target.value) || 0)) })} />
        <span style={styles.areaUnit}>h/day</span>
      </div>
      <div style={styles.thermalRow}>
        <span style={styles.qLabel}>Lighting share</span>
        <input type="number" min="0" max="100" value={b.lightingSharePct ?? ""} placeholder="65"
          data-testid={`lighting-share-input-${b.id}`} style={styles.areaInput}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => updateSystemBlock(b.id, { lightingSharePct: e.target.value === "" ? null : Math.max(0, Math.min(100, Number(e.target.value) || 0)) })} />
        <span style={styles.areaUnit}>%</span>
      </div>
      <div style={styles.thermalHint}>Default 16 h/day (leafy · ~12 h fruiting), 65% of load to lighting. Splits this instance&apos;s energy into day/night buckets.</div>
    </div>
  );
}


export function BlockCard({ b, kb, active, onSelect, onRemove, onAddCrop, onRemoveCrop, onShare, onReset }) {
  const dem = blockDemand(b, kb);
  const system = kb?.systems?.find((s) => s.id === b.systemId);
  const allocDraw = useStudio((s) => s.allocDraw);
  const setAllocDraw = useStudio((s) => s.setAllocDraw);
  const setBlockAllocated = useStudio((s) => s.setBlockAllocated);
  const assignBlockSystem = useStudio((s) => s.assignBlockSystem);
  const beginStackAt = useStudio((s) => s.beginStackAt);
  const systemBlocks = useStudio((s) => s.systemBlocks);
  const pickedSuppliers = useStudio((s) => s.pickedSuppliers);
  const supplier = b.systemId ? pickedSuppliers[b.systemId] : null;
  const locationAllocations = useStudio((s) => s.locationAllocations);
  const zoneAllocations = useStudio((s) => s.zoneAllocations);
  const unassigned = !b.systemId;
  // Shared allocation occupancy across all systems in this block's ZONE (multi-zone aware).
  const zid = b.zoneId || b.locationId;
  const sibs = zid ? systemBlocks.filter((x) => (x.zoneId || x.locationId) === zid) : [];
  const locUsed = sibs.reduce((sum, x) => sum + (Number(x.area) || 0), 0);
  const locCap = Number(zoneAllocations?.[zid]) || Number(locationAllocations?.[b.locationId]) || (Number(b.allocatedArea) || 0);
  const locFree = Math.max(0, locCap - locUsed);
  return (
    <div
      data-testid={`system-block-${b.id}`}
      data-block-scroll={b.id}
      style={{ ...styles.blockCard, borderColor: active ? colors.pillar.systems : (unassigned ? MINT : colors.border), boxShadow: active ? `0 2px 10px ${colors.pillar.systems}22` : shadows.soft }}
      onClick={() => onSelect(b.id)}
    >
      <div style={styles.blockCardTop}>
        <BlockTitle b={b} />
        <button data-testid={`system-block-remove-${b.id}`} style={styles.blockRemove} onClick={(e) => { e.stopPropagation(); onRemove(b.id); }}>Remove</button>
      </div>
      {unassigned && (
        <div style={styles.biaAssign} data-testid={`block-assign-system-${b.id}`} onClick={(e) => e.stopPropagation()}>
          <span style={styles.biaAssignLabel}>Existing volume · define system</span>
          <select data-testid={`block-system-select-${b.id}`} value=""
            onChange={(e) => { if (e.target.value) assignBlockSystem(b.id, e.target.value); }}
            style={styles.biaAssignSelect}>
            <option value="">Choose system…</option>
            {(kb?.systems || []).map((s) => (<option key={s.id} value={s.id}>{s.label}</option>))}
          </select>
        </div>
      )}
      <div style={styles.blockMetrics}>
        <Metric label="FOOTPRINT" value={`${b.area} m²`} />
        <Metric label="HEIGHT" value={`${b.height} m`} />
        <Metric label="VOLUME" value={`${b.volume} m³`} />
      </div>
      {b.allocatedArea != null && b.allocatedArea > 0 && (
        <div style={styles.blockAlloc} data-testid={`block-alloc-${b.id}`}>
          Allocated zone <b>{Math.round(b.allocatedArea).toLocaleString()} m²</b>
          {b.area > b.allocatedArea + 0.5
            ? <span style={styles.blockAllocWarn} data-testid={`block-alloc-warn-${b.id}`}>⚠ footprint over by {Math.round(b.area - b.allocatedArea).toLocaleString()} m²</span>
            : <span style={styles.blockAllocOk}>{Math.round((b.area / b.allocatedArea) * 100)}% packed</span>}
        </div>
      )}
      {supplier && (
        <div style={styles.blockSupplier} data-testid={`block-supplier-${b.id}`}>
          <span style={styles.blockSupplierLabel}>SUPPLIER</span>
          <span style={styles.blockSupplierName}>{supplier.name}</span>
          {supplier.website && (
            <a href={supplier.website} target="_blank" rel="noreferrer" style={styles.blockSupplierLink} onClick={(e) => e.stopPropagation()}>site ↗</a>
          )}
        </div>
      )}
      {b.locationId && locCap > 0 && (
        <div style={styles.stackRow} data-testid={`block-stack-row-${b.id}`}>
          <span style={styles.stackMeta}>
            {b.locationLabel || "Location"}: {sibs.length} system{sibs.length === 1 ? "" : "s"} · {Math.round(locUsed).toLocaleString()}/{Math.round(locCap).toLocaleString()} m² · <b style={{ color: locFree > 0 ? PILLAR_BY_ID.crops.solid : "#FF3B30" }}>{Math.round(locFree).toLocaleString()} free</b>
          </span>
          <button data-testid={`block-stack-${b.id}`} style={styles.stackBtn}
            onClick={(e) => { e.stopPropagation(); beginStackAt(zid); }}>
            ＋ Add system to this allocation
          </button>
        </div>
      )}
      {active && (
        <div style={styles.blockAllocEdit} data-testid={`block-alloc-edit-${b.id}`} onClick={(e) => e.stopPropagation()}>
          <span style={styles.overrideLabel}>Allocation zone</span>
          <input type="number" min="0" value={b.allocatedArea ?? ""} placeholder="m²" data-testid={`block-alloc-input-${b.id}`}
            onChange={(e) => setBlockAllocated(b.id, Math.max(0, parseFloat(e.target.value) || 0))} style={styles.funnelInput} />
          <span style={styles.areaUnit}>m²</span>
          <button data-testid={`block-alloc-draw-${b.id}`}
            onClick={() => { onSelect(b.id); setAllocDraw(allocDraw ? null : { origin: "systems" }); }}
            style={{ ...styles.pickBtn, marginLeft: 8, ...(allocDraw ? { background: PILLAR_BY_ID.locations.solid, color: "#fff", borderColor: PILLAR_BY_ID.locations.solid } : {}) }}>
            {allocDraw ? "◉ Draw zone… Esc" : "◎ Draw zone"}
          </button>
          <div style={{ flexBasis: "100%" }}><ShapeModeToggle /></div>
        </div>
      )}
      {dem.has
        ? <div style={styles.baselineRow} data-testid={`block-baseline-${b.id}`}>{(dem.energyKwh / (b.area || 1)).toFixed(1)} kWh/m² · {(dem.waterL / (b.area || 1)).toFixed(1)} L/m²</div>
        : <div style={styles.baselineHint} data-testid={`block-baseline-${b.id}`}>Add crops below to record this hardware&apos;s water and energy intensity.</div>}
      {active && system && <SystemBaselines system={system} area={b.area} block={b} />}
      {active && <BlockDims b={b} />}
      {active && system && systemNeedsGrowLights(system) && <GrowLightSchedule b={b} />}
      {active && (
        <CropAllocationCard
          block={b}
          kb={kb}
          onAdd={(cid) => onAddCrop(b.id, cid)}
          onRemoveCrop={(cid) => onRemoveCrop(b.id, cid)}
          onShare={(cid, pct) => onShare(b.id, cid, pct)}
          onReset={() => onReset(b.id)}
          onDone={() => onSelect(null)}
        />
      )}
    </div>
  );
}
// PHASE 3 — the single source of truth. Every paired Location+System instance, listed at the top
// of BOTH the Location and System tabs; fully visible & editable from either. Reuses BlockCard for
// crop allocation / height / removal.
// Compact summary tile for the collapsed group card. Highlighted when its ring is active.
function SummaryTile({ label, value, sub, active, accent, testId }) {
  return (
    <div data-testid={testId} style={{ ...styles.metric, ...(active ? { background: `${accent}14`, boxShadow: `inset 0 0 0 1.5px ${accent}` } : {}) }}>
      <div style={{ ...styles.metricLabel, ...(active ? { color: accent } : {}) }}>{label}</div>
      <div style={{ ...styles.metricValue, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</div>
      {sub ? <div style={styles.tileSub}>{sub}</div> : null}
    </div>
  );
}

// A "card" = a Zone → its System(s) → their Crop(s). Collapsed shows Zone / System / Crop tiles
// (the tile matching the active ring is highlighted). Expanding reveals the member system editors,
// so multi-system / multi-crop zones surface as "Varies" until opened.
function GroupCard({ group, kb, rootPillar, zoneAllocations, zoneLabels, activeBlockId, onSelect, onRemove, onAddCrop, onRemoveCrop, onShare, onReset, onRenameZone, onDeleteZone }) {
  const blocks = group.blocks;
  const hasZone = !!(group.zoneId || group.locationId);
  const zoneArea = hasZone ? (Number(zoneAllocations?.[group.key]) || blocks.reduce((m, b) => Math.max(m, Number(b.allocatedArea) || 0), 0) || 0) : 0;
  const usedFootprint = blocks.reduce((a, b) => a + (Number(b.area) || 0), 0);
  const util = zoneArea > 0 ? Math.round((usedFootprint / zoneArea) * 100) : null;
  const assigned = blocks.filter((b) => b.systemId);
  const sysLabels = [...new Set(assigned.map((b) => b.systemLabel || b.systemId))];
  const cropIds = [...new Set(blocks.flatMap((b) => Object.keys(b.allocations || {}).filter((k) => (b.allocations[k] || 0) > 0)))];
  const cropLabel = (cid) => { const c = kb?.crops?.find((x) => x.id === cid); return c ? (c.cultivar || c.crop) : cid; };
  const baseZoneLabel = blocks.find((b) => b.locationLabel)?.locationLabel || null;
  const autoLabel = baseZoneLabel ? (group.zoneIndex ? `${baseZoneLabel} · Zone ${group.zoneIndex}` : baseZoneLabel) : null;
  const customLabel = zoneLabels?.[group.key];
  const zoneLabel = customLabel || autoLabel;
  const isRealZone = !String(group.key).startsWith("solo-");
  const expanded = blocks.some((b) => b.id === activeBlockId);
  const toggle = () => (expanded ? onSelect(null) : onSelect(blocks[0].id));
  const [editing, setEditing] = React.useState(false);
  const [draftName, setDraftName] = React.useState("");
  const commitRename = () => { onRenameZone?.(group.key, draftName); setEditing(false); };

  const P = { locations: PILLAR_BY_ID.locations.solid, systems: PILLAR_BY_ID.systems.solid, crops: PILLAR_BY_ID.crops.solid };
  const accent = P[rootPillar] || colors.border;
  const title = zoneLabel || sysLabels[0] || "Unzoned";
  const zoneVal = zoneArea > 0 ? `${Math.round(zoneArea).toLocaleString()} m²` : "Not set";
  const sysVal = sysLabels.length === 0 ? "—" : sysLabels.length === 1 ? sysLabels[0] : `Varies · ${sysLabels.length}`;
  const sysSub = assigned.length ? `${Math.round(usedFootprint).toLocaleString()} m² footprint` : null;
  const cropVal = cropIds.length === 0 ? "None" : cropIds.length === 1 ? cropLabel(cropIds[0]) : `Varies · ${cropIds.length}`;
  const doDelete = (e) => {
    e.stopPropagation();
    const n = blocks.length;
    if (n === 0 || window.confirm(`Delete "${title}" and its ${n} system${n === 1 ? "" : "s"}? This cannot be undone.`)) onDeleteZone?.(group.key);
  };

  return (
    <div data-testid={`group-card-${group.key}`} style={{ ...styles.blockCard, cursor: "default", borderColor: expanded ? accent : colors.border, boxShadow: expanded ? `0 2px 10px ${accent}22` : shadows.soft }}>
      <div style={{ ...styles.blockCardTop, cursor: "pointer" }} data-testid={`group-header-${group.key}`} onClick={editing ? undefined : toggle}>
        {editing ? (
          <input autoFocus data-testid={`group-rename-input-${group.key}`} value={draftName}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") setEditing(false); }}
            onBlur={commitRename}
            placeholder="Zone name" style={styles.groupRenameInput} />
        ) : (
          <span style={styles.blockTitle}>{title}</span>
        )}
        <span style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
          {hasZone && zoneArea > 0 && !editing && (
            <span style={styles.groupUtil} data-testid={`group-util-${group.key}`}>{Math.round(usedFootprint).toLocaleString()} / {Math.round(zoneArea).toLocaleString()} m² · {util}%</span>
          )}
          {isRealZone && !editing && (
            <button data-testid={`group-rename-${group.key}`} title="Rename zone" style={styles.groupIconBtn}
              onClick={(e) => { e.stopPropagation(); setDraftName(customLabel || autoLabel || ""); setEditing(true); }}>
              <Pencil size={13} />
            </button>
          )}
          {isRealZone && !editing && (
            <button data-testid={`group-delete-${group.key}`} title="Delete this zone and its systems" style={styles.groupIconBtn}
              onClick={doDelete}>
              <Trash2 size={13} />
            </button>
          )}
          {!editing && <span style={styles.groupChevron}>{expanded ? "▾" : "▸"}</span>}
        </span>
      </div>
      <div style={styles.blockMetrics} onClick={toggle}>
        <SummaryTile testId={`group-tile-zone-${group.key}`} label="ZONE" value={zoneVal} active={rootPillar === "locations"} accent={P.locations} />
        <SummaryTile testId={`group-tile-system-${group.key}`} label="SYSTEM" value={sysVal} sub={sysSub} active={rootPillar === "systems"} accent={P.systems} />
        <SummaryTile testId={`group-tile-crop-${group.key}`} label="CROP" value={cropVal} active={rootPillar === "crops"} accent={P.crops} />
      </div>
      {expanded && (
        <div style={styles.groupMembers} data-testid={`group-members-${group.key}`}>
          {blocks.map((b) => (
            <BlockCard key={b.id} b={b} kb={kb} active={b.id === activeBlockId}
              onSelect={onSelect} onRemove={onRemove} onAddCrop={onAddCrop}
              onRemoveCrop={onRemoveCrop} onShare={onShare} onReset={onReset} />
          ))}
        </div>
      )}
    </div>
  );
}

export function PairedInstances({ kb, blocks, activeBlockId, onSelect, onRemove, onAddCrop, onRemoveCrop, onShare, onReset }) {
  const rootPillar = useStudio((s) => s.rootPillar);
  const zoneAllocations = useStudio((s) => s.zoneAllocations);
  const zoneLabels = useStudio((s) => s.zoneLabels);
  const renameZone = useStudio((s) => s.renameZone);
  const deleteZone = useStudio((s) => s.deleteZone);
  if (!blocks || !blocks.length) {
    return (
      <div data-testid="paired-instances-empty" style={{ padding: "16px 12px", borderRadius: 10, background: "#f5f7f9", border: `1px dashed ${colors.border}`, fontSize: 12, color: colors.textSecondary, textAlign: "center" }}>
        No paired instances yet
      </div>
    );
  }
  // Group every instance by its ZONE (zoneId, falling back to locationId for legacy blocks); a
  // zone-less instance is its own singleton card. Multiple zones on one location = multiple cards.
  const groupMap = new Map();
  blocks.forEach((b) => {
    const key = (b.zoneId || b.locationId) || `solo-${b.id}`;
    if (!groupMap.has(key)) groupMap.set(key, { key, locationId: b.locationId || null, zoneId: b.zoneId || null, blocks: [] });
    groupMap.get(key).blocks.push(b);
  });
  const groups = [...groupMap.values()];
  // Suffix titles when a location hosts multiple zones, so the cards read distinctly.
  const perLoc = {}; groups.forEach((g) => { if (g.locationId) perLoc[g.locationId] = (perLoc[g.locationId] || 0) + 1; });
  const seen = {}; groups.forEach((g) => { if (g.locationId && perLoc[g.locationId] > 1) { seen[g.locationId] = (seen[g.locationId] || 0) + 1; g.zoneIndex = seen[g.locationId]; } });
  const totalArea = blocks.reduce((a, b) => a + (b.area || 0), 0);
  return (
    <div style={styles.pairedWrap} data-testid="paired-instances">
      <div style={styles.instancesHead}>{groups.length} CARD{groups.length === 1 ? "" : "S"} · {Math.round(totalArea)} m²</div>
      {groups.map((g) => (
        <GroupCard key={g.key} group={g} kb={kb} rootPillar={rootPillar} zoneAllocations={zoneAllocations}
          zoneLabels={zoneLabels} onRenameZone={renameZone} onDeleteZone={deleteZone}
          activeBlockId={activeBlockId} onSelect={onSelect} onRemove={onRemove} onAddCrop={onAddCrop}
          onRemoveCrop={onRemoveCrop} onShare={onShare} onReset={onReset} />
      ))}
    </div>
  );
}

export function SystemBlocksPanel({ kb, blocks, activeBlockId, onSelect, onRemove, onAddCrop, onRemoveCrop, onShare, onReset }) {
  if (!blocks || blocks.length === 0) return null;
  const totalVol = blocks.reduce((a, b) => a + (b.volume || 0), 0);
  const totalArea = blocks.reduce((a, b) => a + (b.area || 0), 0);
  return (
    <div data-testid="system-blocks-panel">
      <div style={styles.sectionHead}>
        <span style={styles.multiHint}>{blocks.length} block{blocks.length === 1 ? "" : "s"} · {Math.round(totalArea)} m² · {totalVol.toFixed(1)} m³</span>
      </div>
      {blocks.map((b) => (
        <BlockCard key={b.id} b={b} kb={kb} active={b.id === activeBlockId}
          onSelect={onSelect} onRemove={onRemove} onAddCrop={onAddCrop}
          onRemoveCrop={onRemoveCrop} onShare={onShare} onReset={onReset} />
      ))}
    </div>
  );
}
export function CropAllocationCard({ block, kb, onAdd, onRemoveCrop, onShare, onReset, onDone }) {
  const updateSystemBlock = useStudio((s) => s.updateSystemBlock);
  const overrides = block.overrides || null;
  const fileRef = React.useRef(null);

  const handleDownload = () => {
    const m = overrides || systemDefaultMetrics(block, kb);
    const rows = [["Metric", "Value", "Unit"], ...OVERRIDE_METRIC_ROWS.map((r) => [r.label, m[r.key] ?? 0, r.unit])];
    const csv = rows.map((r) => r.join(",")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(block.systemLabel || "system").replace(/[^a-z0-9]+/gi, "_")}_performance.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };
  const handleUpload = (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || "");
      const byLabel = Object.fromEntries(OVERRIDE_METRIC_ROWS.map((r) => [r.label.toLowerCase(), r.key]));
      const parsed = {};
      text.split(/\r?\n/).slice(1).forEach((ln) => {
        const cols = ln.split(",");
        const key = byLabel[(cols[0] || "").trim().toLowerCase()];
        const val = parseFloat(cols[1]);
        if (key && !Number.isNaN(val)) parsed[key] = val;
      });
      if (Object.keys(parsed).length) updateSystemBlock(block.id, { overrides: parsed });
    };
    reader.readAsText(file);
    ev.target.value = "";
  };

  const crops = kb?.crops || [];
  const dbs = block.dbSystems || [];
  const yieldFor = (crop) => {
    for (const s of dbs) { if (crop.systems?.[s]?.yield != null) return crop.systems[s].yield; }
    return null;
  };
  const added = Object.keys(block.allocations || {});
  const available = crops.filter((c) => yieldFor(c) != null && !added.includes(c.id));
  const entries = added
    .map((cid) => { const c = crops.find((x) => x.id === cid); return c ? { cid, crop: c, pct: block.allocations[cid] || 0, y: yieldFor(c) || 0 } : null; })
    .filter(Boolean);
  const used = entries.reduce((a, e) => a + e.pct, 0);
  const free = Math.max(0, 100 - used);
  const totalYield = entries.reduce((a, e) => a + (block.area * e.pct / 100) * (e.y || 0), 0);

  return (
    <div style={styles.allocCard} data-testid={`crop-alloc-${block.id}`} onClick={(e) => e.stopPropagation()}>
      <div style={styles.allocHead}>
        <span style={styles.allocTitle}>Crop Yield Allocation</span>
        <div style={styles.allocHeadRight}>
          <button style={styles.microIcon} data-testid={`download-template-${block.id}`} title="Download performance template (.csv, opens in Excel)" onClick={handleDownload}><Download size={13} /></button>
          <button style={styles.microIcon} data-testid={`upload-data-${block.id}`} title="Upload consultant data (.csv); overrides this instance only" onClick={() => fileRef.current?.click()}><Upload size={13} /></button>
          <input ref={fileRef} type="file" accept=".csv,text/csv" style={{ display: "none" }} onChange={handleUpload} data-testid={`upload-input-${block.id}`} />
          <span style={styles.allocArea}>{block.area} m²</span>
        </div>
      </div>
      {overrides && (
        <div style={styles.customBadge} data-testid={`custom-profile-badge-${block.id}`}
          title={`Consultant profile · Yield ${overrides.yield ?? "—"} kg/m² · Water ${overrides.water_use ?? "—"} L/kg · Energy ${overrides.energy_use ?? "—"} kWh/kg · Carbon ${overrides.ghg ?? "—"} kg CO₂e/kg · HVAC ${overrides.hvac_load ?? "—"} kW/100m²`}>
          <BadgeCheck size={12} /> Custom Performance Profile Active
          <button style={styles.badgeClear} data-testid={`clear-override-${block.id}`} onClick={(e) => { e.stopPropagation(); updateSystemBlock(block.id, { overrides: null }); }}>Reset to defaults</button>
        </div>
      )}
      {overrides && (() => {
        const warns = ["yield", "water_use", "energy_use", "ghg"]
          .map((k) => { const b = outOfBounds(k, overrides[k]); return b ? { k, b } : null; })
          .filter(Boolean);
        if (!warns.length) return null;
        return (
          <div style={styles.overrideBoundWarn} data-testid={`override-bound-warn-${block.id}`}>
            ⚠ Input outside standard peer-reviewed bounds:
            {warns.map(({ k, b }) => (
              <div key={k} style={styles.overrideBoundRow}>
                {k.replace("_", " ")} {b.value} {b.unit} is {b.side === "below" ? "below" : "above"} the expected {b.min}–{b.max} ({b.ref})
              </div>
            ))}
          </div>
        );
      })()}
      <select
        data-testid={`add-crop-${block.id}`}
        value=""
        onChange={(e) => { if (e.target.value) onAdd(e.target.value); }}
        style={styles.addCropSelect}
      >
        <option value="">+ Add crop…</option>
        {available.map((c) => (<option key={c.id} value={c.id}>{`${c.crop} · ${c.cultivar}`}</option>))}
      </select>

      {entries.length === 0 && <div style={styles.allocEmpty}>Add a crop to allocate its share of this system&apos;s area.</div>}

      {entries.map((e) => {
        const Icon = cropIcon(e.crop);
        const allocYield = (block.area * e.pct / 100) * (e.y || 0);
        const maxForThis = Math.min(100, e.pct + free);
        return (
          <div key={e.cid} style={styles.cropRow} data-testid={`crop-row-${e.cid}`}>
            <div style={styles.cropRowTop}>
              <span style={styles.cropIconWrap}><Icon size={15} color={colors.pillar.crops} strokeWidth={2.2} /></span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={styles.cropName}>{e.crop.crop} <span style={styles.cropCultivar}>{e.crop.cultivar}</span></div>
                <div style={styles.cropMax}>System max: {e.y} kg/m²</div>
              </div>
              <button style={styles.cropDel} data-testid={`crop-del-${e.cid}`} onClick={() => onRemoveCrop(e.cid)}><X size={13} /></button>
            </div>
            <input
              type="range" min="0" max="100" step="1" value={e.pct}
              data-testid={`crop-share-${e.cid}`}
              onChange={(ev) => onShare(e.cid, Math.min(+ev.target.value, maxForThis))}
              style={{ ...styles.cropSlider, accentColor: colors.pillar.crops }}
            />
            <div style={styles.cropStats}>
              <span style={styles.cropPct} data-testid={`crop-pct-${e.cid}`}>{e.pct}%</span>
              <span style={{ ...styles.cropYield, color: MINT }} data-testid={`crop-yield-${e.cid}`}>{allocYield.toFixed(1)} kg</span>
            </div>
          </div>
        );
      })}

      <div style={styles.allocFooter}>
        <FooterStat label="Used" value={`${used}%`} />
        <FooterStat label="Total Yield" value={`${(overrides?.yield != null ? block.area * overrides.yield : totalYield).toFixed(1)} kg`} accent />
        <FooterStat label="Free" value={`${free}%`} />
      </div>
      {overrides?.yield != null && <div style={styles.overrideNote}>Using the uploaded consultant sheet instead of crop defaults.</div>}
      <div style={styles.allocActions}>
        {entries.length > 0 && (
          <button style={styles.resetBtn} data-testid={`reset-shares-${block.id}`} onClick={(e) => { e.stopPropagation(); onReset(); }}>Reset shares</button>
        )}
        <button style={styles.doneCollapseBtn} data-testid={`draft-done-${block.id}`} onClick={(e) => { e.stopPropagation(); onDone?.(); }}>Collapse</button>
      </div>
    </div>
  );
}
